import { FieldDetail, fieldsApi } from '@/api/fields';
import { createWell } from '@/api/wells';
import { WellCreate } from '@/types/well';
import { dateInputToIso } from '@/utils/wellDates';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface CreateWellModalProps {
  open: boolean;
  onClose: () => void;
  fieldId: string;
  onWellCreated?: () => void;
}

export const CreateWellModal: React.FC<CreateWellModalProps> = ({
  open,
  onClose,
  fieldId,
  onWellCreated,
}) => {
  const [formData, setFormData] = useState<WellCreate>({
    name: '',
    field_id: fieldId,
    type_id: '',
    layer_id: '',
    entry_date: null,
    shut_down_date: null,
  });
  const [entryDate, setEntryDate] = useState('');
  const [shutDownDate, setShutDownDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldDetail, setFieldDetail] = useState<FieldDetail | null>(null);
  const [loadingField, setLoadingField] = useState(false);

  useEffect(() => {
    if (open && fieldId) {
      loadFieldDetail();
    }
  }, [open, fieldId]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      entry_date: dateInputToIso(entryDate),
      shut_down_date: dateInputToIso(shutDownDate),
    }));
  }, [entryDate, shutDownDate]);

  const loadFieldDetail = async () => {
    try {
      setLoadingField(true);
      const field = await fieldsApi.getFieldDetail(fieldId);
      setFieldDetail(field);
    } catch (err) {
      console.error('Error loading field detail:', err);
      setError('Failed to load field information');
    } finally {
      setLoadingField(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Well name is required');
      return;
    }

    if (!formData.type_id) {
      setError('Well type is required');
      return;
    }

    if (!formData.layer_id) {
      setError('Layer is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createWell(formData);
      onWellCreated?.();
      handleClose();
    } catch (err) {
      setError('Failed to create well');
      console.error('Error creating well:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      field_id: fieldId,
      type_id: '',
      layer_id: '',
      entry_date: null,
      shut_down_date: null,
    });
    setEntryDate('');
    setShutDownDate('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Create New Well</Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Well Name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Well Type</InputLabel>
              <Select
                value={formData.type_id}
                label="Well Type"
                onChange={(e) => setFormData((prev) => ({ ...prev, type_id: e.target.value }))}
              >
                <MenuItem value="af31c257-6392-4e13-b171-a4494174d229">Production</MenuItem>
                <MenuItem value="64382a75-3c54-4747-b4d4-7df151073604">Injection</MenuItem>
                <MenuItem value="8307a6df-04f3-4907-88e7-ef58b64da14a">Investigation</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Layer</InputLabel>
              <Select
                value={formData.layer_id}
                label="Layer"
                onChange={(e) => setFormData((prev) => ({ ...prev, layer_id: e.target.value }))}
                disabled={loadingField}
              >
                {fieldDetail?.layers.map((layer) => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {layer.name} {layer.type && `(${layer.type})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Entry Date"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Shut Down Date"
              type="date"
              value={shutDownDate}
              onChange={(e) => setShutDownDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingField || !formData.name.trim() || !formData.type_id || !formData.layer_id}
        >
          {loading ? <CircularProgress size={20} /> : 'Create Well'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
